import { CSSProperties } from 'react';

export interface IconProps {
  name: string;
  /** replaces width + height in the svg. If you use actual width/height from figma the icon is too small. Take the larger
  of width + height and use that as the size param */
  size?: number;
  /** important: height and width will be overwritten by size if size is included */
  width?: number;
  /** important: height and width will be overwritten by size if size is included */
  height?: number;
  loaderSize?: { height: number; width: number };
  viewBox?: string;
  backgroundColor?: string;
  color?: string;
  lightModeColor?: string;
  transitionDuration?: CSSProperties['transitionDuration'];
  skeletonLoader?: boolean;
  skeletonLoaderRadius?: string;
  className?: string;
}

export const ICONS = {
  ADD: 'add',
  ADD_VARIANT: 'addVariant',
  ADD_CLOSE_CIRCLE: 'addCloseCircle',
  ANONYMOUS: 'anonymous',
  ANONYMOUS_DISABLED: 'anonymousDisabled',
  API_KEY: 'apiKey',
  ARROW_UP_FULL: 'arrowUpFull',
  ARROW_UP_THIN: 'arrowUpThin',
  ARROW_UP: 'arrowUp',
  ARROW_DOWN: 'arrowDown',
  ARROW_LEFT: 'arrowLeft',
  ARROW_LEFT2: 'arrowLeft2',
  ARROW_LEFT_SHADOW: 'arrowLeftShadow',
  ARROW_RIGHT: 'arrowRight',
  ARROW_LEFT_3: 'arrowLeft3',
  ARROW_RIGHT_3: 'arrowRight3',
  ATTACHMENT: 'attachment',
  KEYBOARD_ARROW_UP: 'keyboardArrowUp',
  KEYBOARD_ARROW_DOWN: 'keyboardArrowDown',
  CAMERA_ON: 'camera',
  CAMERA_OFF: 'cameraOff',
  CHAIN_LINK: 'chainLink',
  CHAT_STYLE_CONVERSATIONAL: 'chatStyleConversational',
  CHAT_STYLE_DEFAULT: 'chatStyleDefault',
  CHECKMARK: 'checkmark',
  CHECK: 'check',
  CHECK_V2: 'check-v2',
  CHECKBOX: 'checkbox',
  CHECKBOX_CHECKED: 'checkboxChecked',
  CHECKBOX_CHECKED_PARTIAL: 'checkboxCheckedPartial',
  CHECKBOX_SWOOSH: 'checkboxSwoosh',
  CHECKBOX_SWOOSH_CHECKED: 'checkboxSwooshChecked',
  COPY: 'copy',
  CLOSE: 'close',
  DISSOLVE: 'dissolve',
  DOWNLOAD: 'download',
  DOUBLE_DONE: 'doubleDone',
  DOWNLOAD_SPEED: 'downloadSpeed',
  DROPBOX: 'dropbox',
  EDIT: 'edit',
  EDIT_SOLID: 'edit-solid',
  EMOJI: 'emoji',
  EYE_DROPPER: 'eyeDropper',
  FOLDER: 'folder',
  FOLDER_2: 'folder2',
  FULLSCREEN: 'fullscreen',
  MINIMIZE_SCREEN: 'minimizeScreen',
  INFO_CIRCLE: 'infoCircle',
  INFO_CIRCLE_DUOTONE: 'infoCircleDuotone',
  INFO_OUTLINE: 'infoOutline', //if this string changes, change it in the API in the "GetBasePageModuleDbGrouping" func
  INFO_OUTLINE_STRAIGHT: 'infoOutlineStraight',
  INTERNET_SPEED: 'internetSpeed',
  KEYBOARD_ARROW_LEFT: 'keyboardArrowLeft',
  KEYBOARD_ARROW_RIGHT: 'keyboardArrowRight',
  MAGIC_LINK: 'magicLink',
  MESSAGE: 'message',
  MESSAGES: 'messages',
  MICROPHONE_ON: 'microphoneOn',
  MICROPHONE_OFF: 'microphoneOff',
  MODERATOR: 'moderator',
  LINK: 'link',
  LIVE: 'live',
  LOG_OUT: 'logOut',
  PEOPLE: 'people',
  PEOPLE2: 'people2',
  PHOTO: 'photo',
  PHOTO_ALT: 'photoAlt',
  POWER: 'power',
  RECYCLE: 'recycle',
  REPLACE: 'replace',
  REACTION_ADD: 'reactionAdd',
  REFRESH: 'refresh',
  REFRESH_CIRCLE: 'refreshCircle',
  REORDER: 'reorder',
  SEARCH: 'search',
  SEARCH_ROUND: 'search-round',
  SEND: 'send',
  SEND_FILL_RIGHT: 'sendFillRight',
  SETTINGS: 'settings',
  SETTINGS_TRANSPARENT: 'settingsTransparent',
  SETTINGS_TRANSPARENT_OUTLINE: 'settingsTransparentOutline',
  SHARE_SCREEN: 'shareScreen',
  SOUND: 'sound',
  THREE_DOTS_VERTICAL: 'threeDotsVertical',
  THREE_DOTS_HORIZONTAL: 'threeDotsHorizontal',
  THREE_LINES_TRIANGLE: 'threeLinesTriangle',
  UNLINK: 'unlink',
  VIEWERS_EYE: 'viewersEye',
  VIEWERS_EYE_OFF: 'viewersEyeOff',
  VIEWERS_EYE_OFF_SOLID: 'viewersEyeOffSolid',
  VIEWERS_EYE_SOLID: 'viewersEyeSolid',
  VIRTUAL_BACKGROUND: 'virtualBackground',
  BOLD: 'bold',
  ITALIC: 'italic',
  UNDERLINE: 'underline',
  UNDO: 'undo',
  REDO: 'redo',
  OL: 'ol',
  UL: 'ul',
  LINE_SPACING: 'lineSpacing',
  ALIGN_RIGHT: 'align-right',
  ALIGN_CENTER: 'align-center',
  ALIGN_LEFT: 'align-left',
  TRASH: 'trash',
  FONT: 'font',
  FONT_AG_FILLED: 'font-ag-filled',
  SHARE: 'share',
  QUESTION: 'question',
  QUESTION_OUTLINED: 'question-outlined',
  QUESTIONS_AND_POSTS: 'questions-and-posts',
  TITLES_ON: 'titlesOn',
  TITLES_OFF: 'titlesOff',
  INVITE: 'invite',
  ADD_INVITE: 'addInvite',
  TAB_CHAT: 'tabChat',
  TAB_FEEDS: 'tabFeeds',
  TAB_MEDIA: 'tabMedia',
  TAB_NOTES: 'tabNotes',
  CHECK_CIRCLE: 'checkCircle',
  CHECK_CIRCLE_FILLED: 'checkCircleFilled',
  CHECK_CIRCLE_OUTLINE: 'checkCircleOutline',
  CHECKLIST: 'checkList',
  REFRESH_TRANSPARENT: 'refreshTransparent',
  SETTINGS_SLIDERS: 'settingsSliders',
  COMPUTER: 'computer',
  PHONE: 'phone',
  EMAIL: 'email',
  RADIO_ON: 'radioOn',
  PLAY: 'play',
  PLAY_ALT: 'play-alt',
  PLAY_SHADOW: 'play-shadow',
  PAUSE: 'pause',
  PAUSE_SHADOW: 'pause-shadow',
  CLOCK: 'clock',
  CLOCK_ALT: 'clock-alt',
  STOP_LIVE: 'stop-live',
  WINDOW: 'window',
  WALLPAPER: 'wallpaper',
  DOCUMENT: 'document',
  DOCUMENTS: 'documents',
  MOVE: 'move',
  UPLOAD: 'upload',
  UPLOAD_V2: 'upload-v2',
  ZOOM_IN: 'zoomIn',
  ZOOM_OUT: 'zoomOut',
  CAMERA_OUTLINE: 'cameraOutline',
  LITTLE_ARROW_UP: 'littleArrowUp',
  LITTLE_ARROW_DOWN: 'littleArrowDown',
  LAYOUT: 'layout',
  LAYOUT_GRID: 'layoutGrid',
  LAYOUT_GRID_EMPTY: 'layoutGridEmpty',
  LAYOUT_LIST: 'layoutList',
  LAYOUT_LIST_ODD_EVEN: 'layoutListOddEven',
  IMAGE: 'image',
  IMAGE_INVERSE: 'imageInverse',
  FONTS: 'fonts',
  PALETTES: 'palettes',
  PALETTE: 'palette',
  SQUARE_CHECKBOX_ALL: 'squareCheckboxAll',
  SQUARE_CHECKBOX_SELECTED: 'squareCheckboxSelected',
  SQUARE_CHECKBOX_UNSELECTED: 'squareCheckboxUnselected',
  ADD_PRODUCT: 'addProduct',
  NEW_WINDOW: 'newWindow',
  ADD_CATEGORY: 'addCategory',
  PHONE_SLIM: 'phoneSlim',
  TABLET_SLIM: 'tabletSlim',
  DESKTOP_SLIM: 'desktopSlim',
  DRAG_HANDLE: 'dragHandle',
  REPLY: 'Reply',
  REPLY_FILLED: 'ReplyFilled',
  UNDELETE_FILLED: 'UndeleteFilled',
  DONE_FILLED: 'DoneFilled',
  ARROW_RIGHT_TOP_FILLED: 'ArrowRightTopFilled',
  ARROW_RIGHT_BOTTOM_FILLED: 'ArrowRightBottomFilled',
  MODERATION_FILLED: 'ModerationFilled',
  MODERATION_CROSSED_FILLED: 'ModerationCrossedFilled',
  SHIELD: 'Shield',
  CHAT_COMMENT: 'ChatComment',
  CHAT_HIDE: 'ChatHide',
  DIRECT_CHAT: 'DirectChat',
  QUESTION_FILLED: 'QuestionFilled',
  PRIMARY_TOOLTIP: 'PrimaryTooltip',
  CALENDAR: 'Calendar',
  CALENDAR2: 'Calendar2',
  CALENDAR_PLUS: 'CalendarPlus',
  CALENDAR_STARRED: 'calendarStarred',
  SMILEY_FACE: 'smiley-face',
  APPLAUSE: 'applause',
  TWITTER: 'Twitter',
  LINKEDIN: 'LinkedIn',
  WEBSITE: 'Website',
  YOUTUBE: 'YouTube',
  FACEBOOK: 'FaceBook',
  INSTAGRAM: 'Instagram',
  PRODUCT: 'product',
  QUIZ: 'quiz',
  POLL: 'poll',
  POLL_FILLED: 'pollFilled',
  SURVEY: 'survey',
  EVENTS_NAV: 'EventsNav',
  ANALYTICS_NAV: 'AnalyticsNav',
  PEOPLE_NAV: 'PeopleNav',
  PEOPLE_NAV2: 'PeopleNav2',
  CONTENT_NAV: 'ContentNav',
  PRODUCTS_NAV: 'ProductsNav',
  WHATS_NEW: 'WhatsNew',
  SUPPORT: 'Support',
  BELL: 'Bell',
  BELL_EMPTY: 'BellEmpty',
  THEME_PALETTE: 'ThemePalette',
  VERSIONS: 'Versions',
  TRASH_OUTLINE: 'trashOutline',
  PAINTBRUSH: 'Paintbrush',
  ARROW_CIRCLE: 'ArrowCircle',
  DELETE: 'Delete',
  ON: 'On',
  OFF: 'Off',
  CLUB_HOUSE: 'clubHouse',
  OPEN_BOOK: 'openBook',
  PERSON: 'person',
  FILTER_ACTIVE: 'filterActive',
  FILTER_INACTIVE: 'filterInactive',
  DRAG_ARROWS: 'DragArrows',
  PASSWORD: 'password',
  ALIGN_CENTER_ALT: 'align-center-alt',
  ALIGN_LEFT_ALT: 'align-left-alt',
  ALIGN_RIGHT_ALT: 'align-right-alt',
  UNDO_ALT: 'undo-alt',
  REDO_ALT: 'redo-alt',
  ON_DEMAND: 'on-demand',
  PAPERCLIP: 'paperclip',
  CODE: 'code',
  CODE_BLUE_RAZZ: 'code-blue-razz',
  BAR_GRAPH: 'bar-graph', //if this string changes, change it in the API in the "GetBasePageModuleDbGrouping" function
  LAYOUT_NO_TABS: 'layoutNoTabs',
  LAYOUT_HORIZONTAL: 'layoutHorizontal',
  LAYOUT_VERTICAL: 'layoutVertical',
  CHECKBOX_BORDERLESS: 'checkboxBorderless',
  SIGNAL: 'signal', //if this string changes, change it in the API in the "GetBasePageModuleDbGrouping" function
  IMAGES: 'images', //if this string changes, change it in the API in the "GetBasePageModuleDbGrouping" function
  DOCUMENTS_REVERSE: 'documentsReverse',
  URL: 'Url',
  GIF: 'gif',
  GOOGLE_DRIVE: 'google-drive',
  GOOGLE_G_OUTLINE: 'google-g-outline',
  EMOJI_SMILE: 'emoji-smile',
  LIKE: 'like',
  LIKED: 'liked',
  LIKE_2: 'like2',
  COMMENT_EMPTY: 'comment-empty',
  CLOUD_DOWNLOAD: 'icon-cloud-download',
  MUTE: 'mute',
  MUTE_SHADOW: 'mute-shadow',
  MUTED: 'muted',
  MUTED_SHADOW: 'muted-shadow',
  SPEAKER_OFF: 'speakerOff',
  YOUTUBE_OUTLINE: 'youtube-outline',
  GLOBE_OUTLINE: 'globe-outline',
  TWITTER_OUTLINE: 'twitter-outline',
  LINKEDIN_OUTLINE: 'linkedin-outline',
  HOME: 'HOME',
  HOME2: 'HOME2',
  EXPAND: 'EXPAND',
  CONTRACT: 'CONTRACT',
  CALENDAR_ADD: 'calendarAdd',
  REGISTRATION: 'registration',
  BREAKOUT_ROOMS: 'breakoutRooms',
  BUILDING: 'building',
  MAP_ARROW: 'map-arrow',
  ROUND_HOLLOW_STAR: 'round-hollow-star',
  BRIEFCASE: 'briefcase',
  STAR: 'star',
  STAR_OUTLINE: 'starOutline',
  EMAIL_FILLED: 'email-filled',
  EMAIL_SEND: 'email-send',
  REJOIN_LIVE: 'rejoin-live',
  SHOUTOUTS: 'shoutouts',
  HOURGLASS: 'hourglass',
  HYPERLINK: 'hyperlink',
  CAPTIONS: 'captions',
  CAPTIONS_OUTLINE: 'captions-outline',
  BOOKMARK: 'bookmark',
  BOOKMARK_FILLED: 'bookmark-filled',
  IMAGE_CROP: 'image-crop',
  COMMENT_FULL: 'comment-full',
  EMPTY_DM: 'empty-dm',
  EMPTY_DM_SEARCH: 'empty-dm-search',
  NEW_MESSAGE: 'new-message',
  FAST_FORWARD: 'fast-forward',
  FIRESIDE: 'fireside',
  HAND: 'hand',
  HAND_CROSSED: 'hand-crossed',
  HAND_FRONT: 'hand-front',
  HAND_FRONT_OFF: 'hand-front-off',
  HEART_FILLED: 'heart-filled',
  HEART_FILLED_OUTLINE: 'heart-filled-outline',
  COPY_FILLED: 'copy-filled',
  CAUTION: 'caution',
  THEMES: 'themes',
  TEMPLATES: 'templates',
  TEMPLATE_2: 'template-2',
  TEMPLATES3: 'templates3',
  TEMPLATES4: 'templates4',
  TEMPLATES_5_LIGHT_MODE: 'templates5-light-mode',
  TEMPLATES_5_DARK_MODE: 'templates5-dark-mode',
  MAGIC_LINK_SPARKLE: 'magic-link-sparkle',
  EXCLAMATION_MARK: 'exclamation-mark',
  ANGLE_LEFT: 'angle-left',
  ANGLE_RIGHT: 'angle-right',
  LEADERBOARD_NAV: 'leaderboard-nav',
  TROPHY_FILLED: 'trophy-filled',
  GLOBE: 'globe',
  TABLE_FILTER: 'table-filter',
  LINE_GRAPH: 'line-graph',
  LINE_GRAPH_2: 'line-graph-2',
  PIE_CHART: 'pie-chart',
  PIE_CHART_2: 'pie-chart-2',
  DONE: 'done',
  FILTER: 'filter',
  MENU: 'menu',
  MENU_OPEN: 'menu-open',
  MENU_CLOSED: 'menu-closed',
  MENU_NO_SHADOW: 'menu-no-shadow',
  MENU_VER_CLOSE: 'menu-ver-close',
  MENU_HOR_CLOSE: 'menu-hor-close',
  BACK_TEN: 'back-ten',
  FWD_TEN: 'fwd-ten',
  CHAT_DIALOG: 'chat-dialog',
  CHAT_DIALOG_EXIT: 'chat-dialog-exit',
  SCISSORS: 'scissors',
  SEEK_START: 'seek-start',
  SETTINGS_HD: 'settings-hd',
  SETTINGS_SD: 'settings-sd',
  SETTINGS_4K: 'settings-4k',
  SETTINGS_SHADOW: 'settings-shadow',
  SETTINGS_NO_SHADOW: 'settings-no-shadow',
  TRANSCRIPT: 'transcript',
  CHROMECAST: 'chrome-cast',
  PIP: 'pip',
  PIP_EXIT: 'pip-exit',
  FULLSCREEN_SQUARE: 'fullscreen-square',
  FULLSCREEN_SQUARE_EXIT: 'fullscreen-square-exit',
  DEF_HD: 'hd',
  DEF_SD: 'sd',
  DEF_4K: '4k',
  TIKTOK: 'tiktok',
  LIGHT_MODE: 'light-mode',
  LIGHT_MODE_RAINBOW: 'light-mode-rainbow',
  DARK_MODE: 'dark-mode',
  DARK_MODE_RAINBOW: 'dark-mode-rainbow',
  ABOVE_THE_FOLD: 'above-the-fold',
  BELOW_THE_FOLD: 'below-the-fold',
  NO_CHAT: 'no-chat',
  OVERLAY_CHAT: 'overlay-chat',
  SIDE_PANEL_CHAT: 'side-panel-chat',
  MESSAGE_FILLED: 'message-filled',
  TV_WATCH: 'tv-watch',
  RSVP_LOGO: 'rsvp-logo',
  PADLOCK: 'padlock',
  SUGGESTED: 'suggested', // this looks exactly like the youtube logo
  UPVOTE: 'upvote',
  INFO_I: 'info-i',
  EXTERNAL_LINK: 'external-link',
  BAN: 'ban',
  BAN_FILLED: 'ban-filled',
  ROUND_CHECK: 'round-check',
  ADD_RAINBOW: 'add-rainbow',
  DOUBLE_ARROW_LEFT: 'double-arrow-left',
  DOUBLE_ARROW_RIGHT: 'double-arrow-right',
  LOCK: 'lock',
  REG_ABOVE_THE_FOLD_L: 'reg-above-the-fold-L',
  REG_ABOVE_THE_FOLD_R: 'reg-above-the-fold-R',
  REG_BELOW_THE_FOLD_L: 'reg-below-the-fold-L',
  REG_BELOW_THE_FOLD_R: 'reg-below-the-fold-R',
  REG_FULL_PAGE_L: 'reg-full-page-L',
  REG_FULL_PAGE_R: 'reg-full-page-R',
  REG_POPUP: 'reg-popup',
  TRANSLATIONS: 'translations',
  DESKTOP: 'desktop',
  TABLET: 'tablet',
  MOBILE: 'mobile',
  PIN: 'pin',
  NOTES: 'notes',
  BOOST: 'boost',
  PUZZLE: 'puzzle',
  MEDIA: 'media',
  VIDEO_FILLED: 'video-filled',
  PIE_CHART_FILLED: 'pie-chart-filled',
  RTMP: 'rtmp',
  TELLY: 'telly',
  TELLY_FACE: 'telly-face',
  EDUCATION_MORTARBOARD: 'education-mortarboard',
  BACKGROUND_FRAME: 'background-frame',
  CORNERS: 'corners',
  CREATIVE_BOARD: 'creative-board',
  STYLEPACKS: 'stylepacks',
  CAMPAIGNS: 'campaigns',
  CONTENT: 'content',
  STATS: 'stats',
  RUN_OF_SHOW: 'run-of-show',
  SEGMENTS: 'segments',
  SCRIPTS: 'scripts',
  SLIDES_2: 'slides2',
  GREENROOM: 'greenroom',
  HIGHLIGHTS: 'highlights',
  MODERATOR_2: 'moderator2',
  LIVE_TRACKER: 'live-tracker',
  CLICK: 'click',
  INTEGRATIONS: 'integrations',
  INTEGRATIONS_2: 'integrations2',
  CONTROL_ROOM: 'control-room',
  MAGIC_WAND: 'magic-wand',
  PREVIEW_NOTEBOOK: 'preview-notebook',
  EARTH_GLOBE: 'earth-globe',
  BOOKMARK_ADD: 'bookmark-add',
  FAVORITE_ADD: 'favorite-add',
  FOLDER_PLUS: 'folder-plus',
  CALENDAR_FLIP_PLUS: 'calendar-flip-plus',
  COPY_DEFAULT: 'copy-default',
  LAB_FLASK_CONICAL: 'lab-flask',
  ARROW_BIG_TURN_RIGHT: 'arrow-big-turn-right',
  HOME_WIFI: 'home-wifi',
  NOTIFICATION_BELL: 'notification-bell',
  DELETE_TRASH_BIN: 'delete-trash-bin',
  GLOBE_CLOCK: 'globeClock',
  CLOCK_2: 'clock2',
  WEBINAR_LOGO: 'webinar-logo',
  BRANDLIVE_LOGO: 'brandlive-logo',
  SIDEBAR_MINIMIZED: 'sidebar-minimized',
  SIDEBAR_MINIMIZED_FULL: 'sidebar-minimized-full',
  SIDEBAR_DEFAULT: 'sidebar-default',
  SIDEBAR_DEFAULT_FULL: 'sidebar-default-full',
  VIDEO_PLAY: 'video-play',
  THREE_DOT_VERTICAL: 'three-dot-vertial',
  ARROW_DOWN_NEW: 'arrow-down-new',
  ARROW_UP_NEW: 'arrow-up-new',
  LAYOUT_GRID_4: 'layout-grid-4',
  LAYOUT_GRID_2: 'layout-grid-2',
  GOOGLE_GROUP: 'google-group',
  KEY: 'key',
  WINDOW_BROWSER: 'window-browser',
  SUN_FULL: 'sun-full',
  MOON_CRESCENT: 'moon-crescent',
  IMAGE_UPLOAD: 'image-upload',
  DELETE_REFRESH: 'delete-refresh',
  CALENDAR_SETTINGS: 'calendar-settings',
  INTERACTIONS: 'interactions',
  STAR_2: 'star2',
  SEND_2: 'send2',
  SEND_3: 'send-3',
  PERSON_CHECKMARK: 'personCheckmark',
  PERSON_ARROW_RIGHT: 'personArrowRight',
  LOGO_UPLOAD: 'logoUpload',
  EMBED: 'embed',
  SELECT_DROPDOWN_CARET: 'selectDropdownCaret',
  WEBINAR_LOGO_FULL: 'webinarLogoFull',
  ROUND_CHECKBOX_SELECTED: 'round-checkbox-selected',
  ROUND_CHECKBOX_UNSELECTED: 'round-checkbox-unselected',
  USER_FILLED: 'user-filled',
  USER_OUTLINE: 'user-outline',
  USER_REGISTERED: 'user-registered',
  IMAGE_MEDIA_ADD: 'image-media-add',
  SORT_UP: 'sort-up',
  SORT_DOWN: 'sort-down',
  WALKUPS: 'walkups',
  COUNTDOWNS: 'countdowns',
  BOOKENDS: 'bookends',
  LOWERS: 'lowers',
  PLAY_SQUARE: 'playSquare',
  ANNOUNCEMENT: 'announcement',
  SETTINGS_V2: 'settingsV2',
  MEMBERS: 'members',
  HISTORY: 'history',
  ACADEMIC_CAP: 'academic-cap',
  BRAND_TAG: 'brand-tag',
  SKEW: 'skew',
  WATERMARK: 'watermark',
  CALENDAR_PLAY_SHOW: 'calendar-play-show',
  AI_SEND_MESSAGE: 'ai-send-message',
  ATTENDEE: 'icon-attendee',
  MENU_VERTICAL2: 'menu-vertical2',
  RATING: 'rating',
  REGISTERED: 'registered',
  RESEND_EMAIL: 'resend-email',
  SHOW_EYE: 'show-eye',
  SPARKLE_AI: 'sparkle-ai',
  USERS_AUDIENCE: 'users-audience',
  VIDEO: 'video',
  GLOBE_PUBLIC: 'globe-public',
  USER_ADD: 'user-add',
  USERS: 'users',
  CHANNEL: 'channel',
  TOAST_INFO: 'toast-info',
  TOAST_WARNING: 'toast-warning',
  TOAST_SUCCESS: 'toast-success',
  TOAST_ERROR: 'toast-error',
  BTV_LOGO: 'btv-logo',
  STUDIO: 'studio',
  VIMEO: 'vimeo',
  MS_TEAMS: 'ms-teams',
  ZOOM: 'zoom',
  LIVE_SIGNAL: 'live-signal',
  TIMEZONE: 'timezone',
  LAYOUT_FLIP_VERTICAL: 'layout-flip-vertical',
  COPY_DUPLICATE: 'copy-duplicate',
  ADD_REGISTRATION: 'add-registration',
  BAR_CHART: 'bar-chart',
  SECURITY_SHIELD: 'security-shield',
  TOOLS: 'tools',
  ADD_FILE_CREATE_NOTE_SCRIPT_DOCUMENT: 'add-file-create-note-script-document',
  COLUMN_SETTINGS: 'icon-column-settings',
  CHAT_DEFAULT: 'chat-default',
  ICON_FILE_DOCUMENT_PAGE_SCRIPT_TEXT_EMPTY: 'file-document-page-script-text-empty',
};

export type IconType = keyof typeof ICONS;
